import { Button, Group, Modal, SimpleGrid, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { useCallback } from 'react';
import { UsStateSelect } from '@/components/UsStateSelect';
import {
  defaultBillingEntityFormValues,
  billingEntitySchema,
  type BillingEntityFormInput,
} from '@/schemas/billingEntity.schema';

export interface AddBillingEntityModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (values: BillingEntityFormInput) => void | Promise<void>;
  initialValues?: BillingEntityFormInput;
  editing?: boolean;
}

export function AddBillingEntityModal({
  opened,
  onClose,
  onSubmit,
  initialValues = defaultBillingEntityFormValues,
  editing = false,
}: AddBillingEntityModalProps): JSX.Element {
  const form = useForm<BillingEntityFormInput>({
    initialValues,
    validate: zod4Resolver(billingEntitySchema),
  });

  const handleClose = useCallback(() => {
    form.reset();
    onClose();
  }, [form, onClose]);

  const handleSubmit = useCallback(
    async (values: BillingEntityFormInput) => {
      await onSubmit(values);
      form.reset();
      onClose();
    },
    [form, onClose, onSubmit],
  );

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={editing ? 'Edit billing entity' : 'Add billing entity'}
      size="lg"
      centered
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <TextInput
            label="Name"
            placeholder="Legal / merchant name"
            withAsterisk
            {...form.getInputProps('name')}
          />
          <TextInput
            label="Stripe Connected-Account ID"
            placeholder="acct_1A2B3C4D"
            withAsterisk
            {...form.getInputProps('stripeConnectedAccountId')}
          />
          <SimpleGrid cols={2}>
            <TextInput
              label="NPI"
              placeholder="1234567890"
              withAsterisk
              {...form.getInputProps('npi')}
            />
            <TextInput
              label="EIN"
              placeholder="12-3456789"
              withAsterisk
              {...form.getInputProps('ein')}
            />
          </SimpleGrid>
          <TextInput
            label="Street address"
            placeholder="123 Main St"
            withAsterisk
            {...form.getInputProps('street')}
          />
          <SimpleGrid cols={3}>
            <TextInput
              label="City"
              placeholder="Oakland"
              withAsterisk
              {...form.getInputProps('city')}
            />
            <UsStateSelect
              label="State"
              withAsterisk
              value={form.values.state ?? ''}
              onChange={(value) => form.setFieldValue('state', value)}
              error={form.errors.state}
            />
            <TextInput
              label="ZIP"
              placeholder="94612"
              withAsterisk
              autoComplete="postal-code"
              {...form.getInputProps('postalCode')}
            />
          </SimpleGrid>
          <TextInput
            label="Phone"
            placeholder="(555) 555-0100"
            {...form.getInputProps('phone')}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit">{editing ? 'Save Changes' : 'Add billing entity'}</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
